// FORMS_V2B — /forms/instellingen: webhooks (en uitleg herinneringen/verloop)
import React, { useEffect, useState, useCallback } from 'react';
import FormsTabs from '../components/FormsTabs';
import api from '../services/api';

const EVENT_LABELS = {
  'document.completed': 'Volledig ondertekend (PDF klaar)',
  'document.signed': 'Eén ondertekenaar heeft getekend',
  'document.declined': 'Geweigerd door ondertekenaar',
  'document.withdrawn': 'Ingetrokken door verzender',
  'document.expired': 'Verlopen'
};

export default function FormsInstellingen() {
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState(Object.keys(EVENT_LABELS));
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [url, setUrl] = useState('');
  const [oms, setOms] = useState('');
  const [sel, setSel] = useState([]);
  const [nieuwSecret, setNieuwSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { const r = await api.get('/forms-workflow/webhooks'); setHooks(r.data.webhooks || []); if (r.data.events) setEvents(r.data.events); }
    catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!/^https:\/\//i.test(url)) { alert('URL moet met https:// beginnen'); return; }
    setBusy(true);
    try {
      const r = await api.post('/forms-workflow/webhooks', { url: url.trim(), omschrijving: oms.trim(), events: sel.length ? sel : null });
      setNieuwSecret({ id: r.data.id, secret: r.data.secret, url: url.trim() });
      setUrl(''); setOms(''); setSel([]);
      await load();
    } catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };
  const toggle = async (h) => { try { await api.put(`/forms-workflow/webhooks/${h.id}`, { actief: !h.actief }); await load(); } catch (e) { alert(e.message); } };
  const del = async (h) => { if (!window.confirm(`Webhook naar ${h.url} verwijderen?`)) return; try { await api.delete(`/forms-workflow/webhooks/${h.id}`); await load(); } catch (e) { alert(e.message); } };
  const test = async (h) => {
    setTestResult(t => ({ ...t, [h.id]: '…' }));
    try { const r = await api.post(`/forms-workflow/webhooks/${h.id}/test`); setTestResult(t => ({ ...t, [h.id]: r.data.success ? `✅ HTTP ${r.data.status}` : `❌ ${r.data.error || 'mislukt'}` })); await load(); }
    catch (e) { setTestResult(t => ({ ...t, [h.id]: '❌ ' + (e.response?.data?.message || e.message) })); }
  };
  const fmt = (d) => d ? new Date(d).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <FormsTabs active="instellingen" title="Instellingen" />

      <div style={card}>
        <h2 style={h2}>⏱ Herinneringen en verloop</h2>
        <p style={p}>Ondertekenaars die niet reageren krijgen automatisch een herinnering na <strong>3 dagen</strong> en nog één na <strong>7 dagen</strong>. Daarna verloopt het document op de gekozen einddatum (standaard 30 dagen, instelbaar per document bij versturen). Je krijgt dan zelf een mail. Handmatig herinneren kan altijd via de inbox.</p>
      </div>

      <div style={card}>
        <h2 style={h2}>🔗 Webhooks</h2>
        <p style={p}>Laat je eigen systeem (CRM, ERP, Zapier/Make, n8n) automatisch reageren op Forms-events. WOMsys stuurt een <code>POST</code> met JSON naar jouw URL, ondertekend met <code>X-WOMsys-Signature: sha256=HMAC(secret, body)</code>. De payload bevat document-id, naam, status, hash, ondertekenaars en een <code>pdf_url</code>/<code>verify_url</code>.</p>

        {err && <div style={{ color: '#991b1b', marginBottom: 10 }}>{err}</div>}
        {loading ? <div style={{ color: '#64748b' }}>Laden…</div> : (
          hooks.length === 0 ? <div style={{ color: '#94a3b8', fontStyle: 'italic', marginBottom: 12 }}>Nog geen webhooks.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
              <thead><tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12 }}>
                <th style={th}>URL</th><th style={th}>Events</th><th style={th}>Laatste</th><th style={th}>Status</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {hooks.map(h => (
                  <tr key={h.id} style={{ borderTop: '1px solid #e2e8f0', opacity: h.actief ? 1 : 0.55 }}>
                    <td style={td}><div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{h.url}</div>{h.omschrijving && <div style={{ color: '#64748b' }}>{h.omschrijving}</div>}<div style={{ color: '#94a3b8', fontSize: 11 }}>secret {h.secret_masked}</div></td>
                    <td style={td}>{(h.events && h.events.length) ? h.events.map(e => <div key={e}>{EVENT_LABELS[e] || e}</div>) : <span style={{ color: '#64748b' }}>alle</span>}</td>
                    <td style={td}>{fmt(h.laatste_poging)}{h.laatste_status ? ` · HTTP ${h.laatste_status}` : ''}{h.laatste_fout && <div style={{ color: '#991b1b' }}>{h.laatste_fout}</div>}{h.fouten_op_rij >= 25 && <div style={{ color: '#991b1b', fontWeight: 600 }}>gepauzeerd na 25 fouten — zet uit/aan om te resetten</div>}</td>
                    <td style={td}><span style={{ background: h.actief ? '#d1fae5' : '#e2e8f0', color: h.actief ? '#065f46' : '#475569', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{h.actief ? 'actief' : 'uit'}</span></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button style={btn} onClick={() => test(h)}>🧪 Test</button>{' '}
                      <button style={btn} onClick={() => toggle(h)}>{h.actief ? 'Uit' : 'Aan'}</button>{' '}
                      <button style={{ ...btn, color: '#991b1b' }} onClick={() => del(h)}>🗑</button>
                      {testResult[h.id] && <div style={{ fontSize: 11, marginTop: 4 }}>{testResult[h.id]}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {nieuwSecret && (
          <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
            <strong>Bewaar dit secret — het wordt maar één keer getoond.</strong>
            <div style={{ fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 6, fontSize: 13 }}>{nieuwSecret.secret}</div>
            <div style={{ color: '#92400e', marginTop: 6 }}>Verifieer aan jouw kant: <code>sha256=HMAC_SHA256(secret, raw_body)</code> == header <code>X-WOMsys-Signature</code>.</div>
            <button style={{ ...btn, marginTop: 8 }} onClick={() => setNieuwSecret(null)}>Gelezen</button>
          </div>
        )}

        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Nieuwe webhook</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://jouw-systeem.nl/webhooks/womsys" style={inp} />
            <input value={oms} onChange={e => setOms(e.target.value)} placeholder="Omschrijving (optioneel)" style={inp} />
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10, fontSize: 13 }}>
            {events.map(e => (
              <label key={e} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={sel.includes(e)} onChange={ev => setSel(s => ev.target.checked ? [...s, e] : s.filter(x => x !== e))} />
                {EVENT_LABELS[e] || e}
              </label>
            ))}
            <span style={{ color: '#94a3b8' }}>(niets aangevinkt = alle events)</span>
          </div>
          <button style={{ ...btn, background: '#1e3a5f', color: 'white', borderColor: '#1e3a5f' }} disabled={busy || !url} onClick={add}>{busy ? 'Bezig…' : '+ Toevoegen'}</button>
        </div>
      </div>

      <ApiKeys />

      <div style={card}>
        <h2 style={h2}>📦 Voorbeeld-payload</h2>
        <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8, fontSize: 12, overflowX: 'auto' }}>{`POST ${'{jouw-url}'}
X-WOMsys-Event: document.completed
X-WOMsys-Delivery: 5c1f…
X-WOMsys-Signature: sha256=…

{
  "event": "document.completed",
  "sent_at": "2026-09-11T08:12:04.000Z",
  "document": {
    "id": 231, "naam": "Werkbon 2026-0912", "workflow_status": "compleet",
    "signing_strength": "basic", "pdf_hash": "…", "content_hash": "…",
    "pdf_url": "https://app.womsys.nl/api/form-submissions/231/pdf",
    "verify_url": "https://app.womsys.nl/v/231",
    "signers": [{ "naam": "J. de Vries", "email": "j@klant.nl", "status": "getekend", "getekend_op": "…" }]
  }
}`}</pre>
        <p style={p}><code>pdf_url</code> vereist een WOMsys-login of API-token; <code>verify_url</code> is publiek.</p>
      </div>
    </div>
  );
}

// FORMS_V2C — API-keys
function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [naam, setNaam] = useState('');
  const [nieuw, setNieuw] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { const r = await api.get('/forms-workflow/api-keys'); setKeys(r.data.keys || []); } catch (_) {} }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setBusy(true);
    try { const r = await api.post('/forms-workflow/api-keys', { naam: naam || 'API key' }); setNieuw(r.data.key); setNaam(''); await load(); }
    catch (e) { alert(e.response?.data?.message || e.message); } finally { setBusy(false); }
  };
  const revoke = async (k) => { if (!window.confirm(`Key "${k.naam}" intrekken? Integraties die 'm gebruiken stoppen direct.`)) return; try { await api.delete(`/forms-workflow/api-keys/${k.id}`); await load(); } catch (e) { alert(e.message); } };
  const fmt = (d) => d ? new Date(d).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
  return (
    <div style={card}>
      <h2 style={h2}>🔑 API-keys (REST-API)</h2>
      <p style={p}>Documenten aanmaken en uitlezen vanuit je eigen software. Stuur <code>Authorization: Bearer wsk_…</code> mee. Basis-URL <code>https://app.womsys.nl/api/v1</code>. Endpoints: <code>POST /documents</code> (multipart: bestand, naam, signers, signing_strength, tekenvolgorde, geldig_dagen), <code>GET /documents</code>, <code>GET /documents/:id</code>, <code>GET /documents/:id/pdf</code>, <code>POST /documents/:id/remind</code>, <code>POST /documents/:id/withdraw</code>. Voorbeeld staat onder dit blok.</p>
      {keys.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
          <thead><tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12 }}><th style={th}>Naam</th><th style={th}>Prefix</th><th style={th}>Door</th><th style={th}>Laatst gebruikt</th><th style={th}>Status</th><th style={th}></th></tr></thead>
          <tbody>{keys.map(k => (
            <tr key={k.id} style={{ borderTop: '1px solid #e2e8f0', opacity: k.actief ? 1 : 0.5 }}>
              <td style={td}>{k.naam}</td><td style={{ ...td, fontFamily: 'monospace' }}>{k.prefix}…</td><td style={td}>{k.gebruiker}</td><td style={td}>{fmt(k.laatst_gebruikt)}</td>
              <td style={td}>{k.actief ? 'actief' : 'ingetrokken'}</td>
              <td style={td}>{k.actief && <button style={{ ...btn, color: '#991b1b' }} onClick={() => revoke(k)}>Intrekken</button>}</td>
            </tr>))}</tbody>
        </table>
      )}
      {nieuw && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
          <strong>Bewaar deze key — hij wordt maar één keer getoond.</strong>
          <div style={{ fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 6 }}>{nieuw}</div>
          <button style={{ ...btn, marginTop: 8 }} onClick={() => setNieuw(null)}>Gelezen</button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={naam} onChange={e => setNaam(e.target.value)} placeholder="Naam (bv. CRM-koppeling)" style={{ ...inp, flex: 1 }} />
        <button style={{ ...btn, background: '#1e3a5f', color: 'white', borderColor: '#1e3a5f' }} disabled={busy} onClick={add}>{busy ? 'Bezig…' : '+ Nieuwe key'}</button>
      </div>
      <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8, fontSize: 12, overflowX: 'auto', marginTop: 12 }}>{`curl -X POST https://app.womsys.nl/api/v1/documents \\
  -H "Authorization: Bearer wsk_…" \\
  -F "bestand=@offerte.pdf" -F "naam=Offerte 2026-091" \\
  -F 'signers=[{"naam":"J. de Vries","email":"j@klant.nl","telefoon":"0612345678"}]' \\
  -F "signing_strength=basic" -F "tekenvolgorde=parallel" -F "geldig_dagen=30"

# antwoord: { "success": true, "submission_id": 231, "signers": [...] }
# zonder coördinaten komt het handtekeningvak rechtsonder op de laatste pagina;
# eigen positie: "signature_field":{"page":2,"x":320,"y":60,"width":180,"height":50}`}</pre>
    </div>
  );
}

const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 18, marginBottom: 16 };
const h2 = { margin: '0 0 8px', fontSize: 16, color: '#1e3a5f' };
const p = { margin: '0 0 12px', fontSize: 13, color: '#475569', lineHeight: 1.5 };
const th = { padding: '6px 8px' };
const td = { padding: '8px', verticalAlign: 'top' };
const btn = { padding: '5px 10px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#475569' };
const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 };
